/**
 * AppShell — the desktop two-column layout.
 *
 * Sidebar (left) + Main (right, scrollable content). The sidebar element is
 * supplied by the host so it can wire its own routing (TanStack Router
 * `<Link>`) into the items.
 *
 * There is no desktop topbar: search lives in the sidebar + the global ⌘/
 * shortcut, notifications live in the sidebar footer, and theme/density moved
 * to Settings → Appearance. Each screen renders its own in-page heading.
 */

import styles from './AppShell.module.css';
import { PreviewLayoutProvider } from './PreviewLayoutContext';

export interface AppShellProps {
  sidebar: React.ReactNode;
  /** Optional preview drawer, rendered as a third column when present. */
  drawer?: React.ReactNode;
  /** Page content. The shell already provides scroll + max-width. */
  children: React.ReactNode;
}

export function AppShell({ sidebar, drawer, children }: AppShellProps) {
  const drawerOpen = !!drawer;
  return (
    <PreviewLayoutProvider open={drawerOpen}>
      <div
        className={`${styles.app} ${drawerOpen ? styles.appWithDrawer : ''}`}
        data-preview-open={drawerOpen ? '' : undefined}
      >
        {sidebar}
        <main className={styles.main}>
          <div className={styles.content}>
            <div className={styles.contentInner}>{children}</div>
          </div>
        </main>
        {drawer && <aside className={styles.drawer}>{drawer}</aside>}
      </div>
    </PreviewLayoutProvider>
  );
}
